import React, { useState, useEffect, useRef } from "react";
import { NavLink } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth } from "../firebase";
import { useTexts } from "../hooks/useTexts";

interface UserDropdownProps {
    user: any;
}

const UserDropdown: React.FC<UserDropdownProps> = ({ user }) => {
    const { getText } = useTexts();
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const menuItems = [
        { to: "/account", icon: "👤", label: getText("user_menu_account", "Mein Account") },
        { to: "/dashboard", icon: "📊", label: getText("user_menu_dashboard", "Dashboard") },
        { to: "/settings", icon: "⚙️", label: getText("user_menu_settings", "Einstellungen") },
    ];

    return (
        <div className="relative" ref={ref}>
            <button
                className="flex items-center gap-2 px-3 py-2 rounded-2xl bg-black/5 border border-gray-200 text-gray-700 hover:bg-black/10 hover:border-gray-300 transition-all duration-200"
                onClick={() => setOpen(!open)}
            >
                <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-blue-500 rounded-lg flex items-center justify-center font-semibold text-white">
                    {user.email?.[0]?.toUpperCase() || "U"}
                </div>
                <svg width="16" height="16" viewBox="0 0 24 24" className={`transition-transform ${open ? 'rotate-180' : ''}`}>
                    <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" />
                </svg>
            </button>

            {open && (
                <div className="absolute top-full right-0 mt-2 w-72 bg-white border border-gray-200 rounded-2xl p-4 shadow-xl z-50">
                    <div className="flex items-center gap-3 p-3 mb-2">
                        <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-blue-500 rounded-xl flex items-center justify-center font-semibold text-xl text-white">
                            {user.email?.[0]?.toUpperCase() || "U"}
                        </div>
                        <div>
                            <div className="font-semibold text-gray-800 text-sm">{user.email}</div>
                            <div className="text-xs text-gray-500">{getText("user_menu_logged_in", "Eingeloggt")}</div>
                        </div>
                    </div>

                    <div className="h-px bg-gray-200 my-3" />

                    {menuItems.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            className="flex items-center gap-3 p-3 rounded-xl hover:bg-black/5 text-gray-600 hover:text-gray-800 transition-all duration-200 text-sm"
                            onClick={() => setOpen(false)}
                        >
                            <span className="text-xl">{item.icon}</span>
                            <span>{item.label}</span>
                        </NavLink>
                    ))}

                    <div className="h-px bg-gray-200 my-3" />
                    <button
                        className="flex items-center gap-3 p-3 rounded-xl hover:bg-red-500/10 text-red-500 hover:text-red-600 w-full transition-all duration-200 text-sm"
                        onClick={() => signOut(auth)}
                    >
                        <span className="text-xl">🚪</span>
                        <span>{getText("user_menu_logout", "Logout")}</span>
                    </button>
                </div>
            )}
        </div>
    );
};

export default UserDropdown;